import { merge } from 'lodash'

class Lightbox {
    /**
     *
     * @param items {HTMLElement|Element|HTMLElement[]|Element[]|NodeList<HTMLElement|Element>}
     * @param options {{captionAttribute: string, isOpenClass: string}}
     */
    constructor(items, options = {}) {
        /**
         * @type {HTMLElement[]}
         */
        this.gallery = items instanceof NodeList ? Array.prototype.slice.call(items) : items instanceof Element ? [items] : items
        /**
         * @type {number}
         */
        this.currentIndex = 0

        /**
         * @type {{captionAttribute: string, isOpenClass: string}}
         */
        this.options = merge({
            galleryAttribute: 'data-gallery',
            captionAttribute: 'title',
            isOpenClass: 'is-open'
        }, options)

        /**
         * @type {{open: boolean}}
         */
        this.state = {
            open: false
        }

        this.createDom()

        this.gallery.forEach((element, index) => {
            element.addEventListener('click', ev => {
                ev.preventDefault()
                this.show(index)
            })
        })
    }

    createDom() {
        /**
         * @type {{container: HTMLElement, panel: HTMLElement, figure: HTMLElement, image: HTMLElement, caption: HTMLElement, close: HTMLElement, controls: HTMLElement, previous: HTMLElement, next: HTMLElement}}
         */
        this.elements = {}
        this.elements.container = document.querySelector('[data-component="lightbox"]')

        if (!this.elements.container) {
            const tmp = document.createElement('div')
            tmp.innerHTML = `
    <div class="lightbox" data-component="lightbox">
        <div class="lightbox__curtain"></div>
        <div class="lightbox__panel" data-component-part="panel">
            <figure class="lightbox__figure" data-component-part="figure">
                <img class="lightbox__image" src="" alt="" data-component-part="image" />
                <div class="lightbox__bottom">
                    <figcaption class="lightbox__caption" data-component-part="caption"></figcaption>
                    <button class="lightbox__close" data-component-part="close"></button>
                </div>
            </figure>
        </div>
        <div class="lightbox__loader" data-component-part="loader">
            <span class="lightbox__dot"></span>
            <span class="lightbox__dot"></span>
            <span class="lightbox__dot"></span>
        </div>
        <div class="lightbox__controls" data-component-part="controls">
            <span class="lightbox__control lightbox__control--left" data-component-part="control" data-direction="<">&lt;</span>
            <span class="lightbox__control lightbox__control--right" data-component-part="control" data-direction=">">&gt;</span>
        </div>
    </div>
            `

            this.elements.container = tmp.firstElementChild
            document.body.append(this.elements.container)
        }

        this.elements.panel = this.elements.container.querySelector('[data-component-part="panel"]')
        this.elements.figure = this.elements.container.querySelector('[data-component-part="figure"]')
        this.elements.image = this.elements.container.querySelector('[data-component-part="image"]')
        this.elements.caption = this.elements.container.querySelector('[data-component-part="caption"]')
        this.elements.captionIndex = this.elements.container.querySelector('[data-component-part="caption-index"]')
        this.elements.close = this.elements.container.querySelector('[data-component-part="close"]')
        this.elements.controls = this.elements.container.querySelector('[data-component-part="controls"]')
        this.elements.previous = this.elements.container.querySelector('[data-component-part="control"][data-direction="<"]')
        this.elements.next = this.elements.container.querySelector('[data-component-part="control"][data-direction=">"]')

        this.elements.panel.addEventListener('click', ev => {
            if (ev.target === this.elements.panel) {
                // Only close, when clicking the background
                this.close()
            }
        })
        this.elements.close.addEventListener('click', () => {
            if (this.isOpen()) {
                this.close()
            }
        })
        this.elements.previous.addEventListener('click', () => {
            if (this.isOpen()) {
                this.show(this.currentIndex - 1)
            }
        })
        this.elements.next.addEventListener('click', () => {
            if (this.isOpen()) {
                this.show(this.currentIndex + 1)
            }
        })

        document.addEventListener('keydown', ev => {
            if (ev.key === 'Escape' && this.isOpen()) {
                this.close()
            }
            if (ev.key === 'ArrowRight' && this.isOpen()) {
                this.show(this.currentIndex + 1)
            }
            if (ev.key === 'ArrowLeft' && this.isOpen()) {
                this.show(this.currentIndex - 1)
            }
        })
    }

    open() {
        this.elements.container.classList.add(this.options.isOpenClass)
        this.elements.figure.style.width = window.getComputedStyle(this.elements.figure).width
        this.elements.figure.style.height = window.getComputedStyle(this.elements.figure).height
        this.state.open = true
        document.body.style.overflow = 'hidden'
    }

    isOpen() {
        return this.state.open
    }

    close() {
        this.elements.container.classList.add('is-in-transition')
        this.elements.container.addEventListener('transitionend', () => {
            this.elements.container.classList.remove('is-in-transition')
            this.elements.figure.style.width = 'auto'
            this.elements.figure.style.height = 'auto'
            this.elements.caption.innerHTML = ''
            this.elements.image.setAttribute('src', '')
        }, { once: true })
        this.elements.container.classList.remove(this.options.isOpenClass)
        this.state.open = false
        document.body.style.overflow = 'auto'
    }

    show(index) {
        if (index >= 0 && index < this.gallery.length) {
            if (!this.isOpen()) {
                this.elements.container.classList.add('is-opening')
                this.open()
            }
            this.currentIndex = index

            const src = this.gallery[index].getAttribute('href')
            const caption = this.gallery[index].getAttribute(this.options.captionAttribute)

            this.elements.container.classList.add('is-loading')

            const loadingAnimationDuration = 1500
            const lastCycleDuration = loadingAnimationDuration * 0.6 + 400
            const loadStart = Date.now()

            this.elements.image.addEventListener('load', () => {
                let elapsedTime = Date.now() - loadStart
                let timeToFullAnimationCycle = lastCycleDuration - Math.min(lastCycleDuration, elapsedTime % loadingAnimationDuration)
                const targetWidth = parseInt(window.getComputedStyle(this.elements.image).width)
                const targetHeight = parseInt(window.getComputedStyle(this.elements.image).height)
                const disableLoadingState = () => {
                    if (!caption) {
                        this.elements.caption.classList.add('is-empty')
                    } else {
                        this.elements.caption.classList.remove('is-empty')
                    }
                    this.elements.container.classList.remove('is-loading')
                    this.elements.container.classList.remove('is-opening')
                }
                if (parseInt(this.elements.figure.style.width) !== targetWidth || parseInt(this.elements.figure.style.height) !== targetHeight) {
                    // If height or width is different, animate and show image afterwards
                    if (this.elements.container.classList.contains('is-opening')) {
                        // If the lightbox is opening, there is not animation.
                        // Just set the height and width and remove the loading animation after next full cycle
                        this.elements.figure.style.width = targetWidth + 'px'
                        this.elements.figure.style.height = targetHeight + 'px'
                        setTimeout(disableLoadingState, timeToFullAnimationCycle)
                    } else {
                        // Wait for width or height transition to finish
                        // and remove loading animation after next full cycle
                        const removeLoadingState = ev => {
                            // Only remove loading state, after the size transition has finished
                            if (ev.propertyName === 'height' || ev.propertyName === 'width') {
                                elapsedTime = Date.now() - loadStart
                                timeToFullAnimationCycle = lastCycleDuration - Math.min(lastCycleDuration, elapsedTime % loadingAnimationDuration)
                                setTimeout(disableLoadingState, timeToFullAnimationCycle)
                                this.elements.figure.removeEventListener('transitionend', removeLoadingState)
                            }
                        }
                        this.elements.figure.addEventListener('transitionend', removeLoadingState)
                        this.elements.figure.style.width = targetWidth + 'px'
                        this.elements.figure.style.height = targetHeight + 'px'
                    }
                } else {
                    setTimeout(disableLoadingState, timeToFullAnimationCycle)
                }
                if (this.gallery.length <= 1) {
                    this.elements.caption.innerHTML = caption
                } else {
                    this.elements.caption.innerHTML = caption + ' ' + '(' + (index + 1) + '/' + this.gallery.length + ')'
                }
            }, { once: true })
            this.elements.image.setAttribute('src', src)
            if (this.gallery.length > 1) {
                this.elements.controls.classList.remove('is-disabled')
                if (index > 0) {
                    this.elements.previous.classList.remove('is-disabled')
                } else {
                    this.elements.previous.classList.add('is-disabled')
                }
                if (index < this.gallery.length - 1) {
                    this.elements.next.classList.remove('is-disabled')
                } else {
                    this.elements.next.classList.add('is-disabled')
                }
            } else {
                this.elements.controls.classList.add('is-disabled')
            }
        }
    }
}

export { Lightbox }
